// Label layout for form fields (z8nrz7djyx): where a label sits beside its
// control, how wide its column is, and how wide a field must be for its control
// to stay usable. Pure, so the renderer and the builder canvas resolve a field
// the same way and the rules are unit-tested (fieldLayout.test.ts).

import type { FormFieldLayout, LabelPosition, LabelWidth, SectionWidth } from '@/types/cgt'

export const LABEL_POSITIONS: readonly LabelPosition[] = ['above', 'left', 'right']
export const LABEL_WIDTHS: readonly LabelWidth[] = ['narrow', 'medium', 'wide']

export const LABEL_POSITION_LABELS: Record<LabelPosition, string> = {
  above: 'Above',
  left: 'Left',
  right: 'Right',
}

export const LABEL_WIDTH_LABELS: Record<LabelWidth, string> = {
  narrow: 'Narrow',
  medium: 'Medium',
  wide: 'Wide',
}

/** Label column width in px: 9rem, 14rem, 20rem. Narrow is today's sm:w-36. */
export const LABEL_WIDTH_PX: Record<LabelWidth, number> = {
  narrow: 144,
  medium: 224,
  wide: 320,
}

// Literal strings so Tailwind's JIT compiler emits them.
export const LABEL_WIDTH_CLASS: Record<LabelWidth, string> = {
  narrow: 'sm:w-36',
  medium: 'sm:w-56',
  wide: 'sm:w-80',
}

/** A control never renders narrower than this. */
export const MIN_CONTROL_WIDTH_PX = 96

/** Gap between a side label and its control (the row's sm:gap-3). */
export const LABEL_ROW_GAP_PX = 12

/**
 * Space the renderer keeps beside a control for its save spinner or "Saved"
 * tick: a 12px icon plus the control column's 8px gap.
 */
export const FIELD_INDICATOR_PX = 20

/** The per-field attachment icon beside a field: 28px button plus an 8px gap. */
export const ATTACHMENT_SLOT_PX = 36

/** An Input's or SelectTrigger's inset around its text: px-3 plus a 1px border, each side. */
export const CONTROL_INSET_PX = 26

/** A SelectTrigger's chevron (16px) plus the space before it. */
export const SELECT_CHEVRON_PX = 24

/**
 * What contentFloorPx needs from the browser. Injected so this module stays
 * pure; the DOM version is lib/controlMeasure.
 */
export interface ControlMeasure {
  /** Width of a native date or datetime picker, which will not draw narrower (z8nrz7e05f). */
  nativePx: (type: 'date' | 'datetime') => number
  /** Width of a line of control text. */
  textPx: (text: string) => number
}

// Single-line controls whose text cannot wrap, so a narrow control cuts it off.
const TEXT_CONTROL_TYPES = new Set(['text', 'number', 'dropdown', 'select'])

/**
 * The narrowest a control can be and still show all of its content
 * (z8nrz7e05f): a date or datetime picker's own width, or the widest text a
 * single-line control holds (its placeholder, or any option of a dropdown)
 * plus its inset. Never below MIN_CONTROL_WIDTH_PX. Other field types wrap or
 * have no text, and keep MIN_CONTROL_WIDTH_PX.
 */
export function contentFloorPx(type: string, texts: readonly string[], measure: ControlMeasure): number {
  if (type === 'date' || type === 'datetime') return Math.max(MIN_CONTROL_WIDTH_PX, Math.ceil(measure.nativePx(type)))
  if (!TEXT_CONTROL_TYPES.has(type)) return MIN_CONTROL_WIDTH_PX
  const widest = texts.reduce((w, text) => (text ? Math.max(w, measure.textPx(text)) : w), 0)
  if (!widest) return MIN_CONTROL_WIDTH_PX
  const inset = type === 'dropdown' || type === 'select' ? CONTROL_INSET_PX + SELECT_CHEVRON_PX : CONTROL_INSET_PX
  return Math.max(MIN_CONTROL_WIDTH_PX, Math.ceil(widest + inset))
}

export const DEFAULT_LABEL_POSITION: LabelPosition = 'left'
export const DEFAULT_LABEL_WIDTH: LabelWidth = 'narrow'

/**
 * Field types that draw a label beside one control and so take a label layout.
 * Takes both vocabularies: the saved one ('dropdown') and the builder's
 * ('select'). Tables and signatures keep their stacked labels; sections, text
 * blocks, process steps and reviews have no label row at all.
 */
const LABEL_LAYOUT_TYPES = new Set([
  'text',
  'number',
  'date',
  'datetime',
  'textarea',
  'checkbox',
  'dropdown',
  'select',
  'calculated',
  'image',
  'attachment',
])

export function supportsLabelLayout(type: string): boolean {
  return LABEL_LAYOUT_TYPES.has(type)
}

export function isLabelPosition(v: unknown): v is LabelPosition {
  return typeof v === 'string' && (LABEL_POSITIONS as readonly string[]).includes(v)
}

export function isLabelWidth(v: unknown): v is LabelWidth {
  return typeof v === 'string' && (LABEL_WIDTHS as readonly string[]).includes(v)
}

/**
 * Keeps only valid keys. Returns null when nothing valid is left, which is the
 * stored shape for "no form default".
 */
export function sanitizeFieldLayout(layout: unknown): FormFieldLayout | null {
  if (!layout || typeof layout !== 'object') return null
  const { labelPosition, labelWidth } = layout as Record<string, unknown>
  const out: FormFieldLayout = {}
  if (isLabelPosition(labelPosition)) out.labelPosition = labelPosition
  if (isLabelWidth(labelWidth)) out.labelWidth = labelWidth
  return out.labelPosition || out.labelWidth ? out : null
}

export interface ResolvedLabelLayout {
  position: LabelPosition
  width: LabelWidth
  /** Where the position/width came from. 'field' when the field sets either key. */
  source: 'field' | 'form' | 'system'
  /**
   * Nothing is set on the field and the form has no default. The renderer then
   * draws exactly what it drew before label layout existed.
   */
  legacy: boolean
}

/** What a field with nothing set on a form with no default resolves to. */
export const SYSTEM_LABEL_LAYOUT: ResolvedLabelLayout = {
  position: DEFAULT_LABEL_POSITION,
  width: DEFAULT_LABEL_WIDTH,
  source: 'system',
  legacy: true,
}

/**
 * Field value wins, then the form default, then the system default. An invalid
 * stored value counts as unset, so it falls through to the next level.
 */
export function resolveLabelLayout(
  field: { labelPosition?: unknown; labelWidth?: unknown },
  formDefault: FormFieldLayout | null | undefined,
): ResolvedLabelLayout {
  const fieldPosition = isLabelPosition(field.labelPosition) ? field.labelPosition : undefined
  const fieldWidth = isLabelWidth(field.labelWidth) ? field.labelWidth : undefined
  const form = sanitizeFieldLayout(formDefault)
  const source = fieldPosition || fieldWidth ? 'field' : form ? 'form' : 'system'
  return {
    position: fieldPosition ?? form?.labelPosition ?? DEFAULT_LABEL_POSITION,
    width: fieldWidth ?? form?.labelWidth ?? DEFAULT_LABEL_WIDTH,
    source,
    legacy: source === 'system',
  }
}

const SPAN_COLUMNS: Record<SectionWidth, number> = { quarter: 3, third: 4, half: 6, full: 12 }
const WIDTHS_NARROW_TO_WIDE: SectionWidth[] = ['quarter', 'third', 'half', 'full']

/** Px width of a field spanning `columns` of a 12-column grid `containerPx` wide. */
export function spanWidthPx(columns: number, containerPx: number, gapPx: number): number {
  return ((containerPx - 11 * gapPx) * columns) / 12 + (columns - 1) * gapPx
}

export interface FieldSpanInput {
  requested: SectionWidth
  position: LabelPosition
  /** Label column width, used only when the label sits left or right. */
  labelPx: number
  /** Measured grid content width; null or 0 while unmeasured. */
  containerPx: number | null
  gapPx: number
  /** Space beside the control that is not the control: indicators, attachment icon, card padding. */
  trailingPx: number
  /** The control's own floor (contentFloorPx). Never below MIN_CONTROL_WIDTH_PX. */
  minControlPx?: number
}

/**
 * The width a field actually takes so its control is at least its floor:
 * MIN_CONTROL_WIDTH_PX, or more when its content needs it. Tries the requested
 * fraction, then each wider one (quarter, third, half, full). A side label that
 * cannot fit even at full width stacks above the control. Unmeasured
 * containers keep the requested width.
 */
export function resolveFieldSpan(input: FieldSpanInput): { width: SectionWidth; stacked: boolean } {
  const { requested, position, labelPx, containerPx, gapPx, trailingPx } = input
  if (!containerPx) return { width: requested, stacked: false }
  const floor = Math.max(MIN_CONTROL_WIDTH_PX, input.minControlPx ?? 0)
  const side = position !== 'above'
  const reserved = side ? labelPx + LABEL_ROW_GAP_PX + trailingPx : trailingPx
  const candidates = WIDTHS_NARROW_TO_WIDE.slice(WIDTHS_NARROW_TO_WIDE.indexOf(requested))
  for (const width of candidates) {
    if (spanWidthPx(SPAN_COLUMNS[width], containerPx, gapPx) - reserved >= floor) {
      return { width, stacked: false }
    }
  }
  return { width: 'full', stacked: side }
}

/**
 * True when a section is too narrow for its 12-column grid: the 11 gaps alone
 * leave no room for a MIN_CONTROL_WIDTH_PX control. Such a section drops to one
 * column, so it never overflows (a quarter section inside a quarter page column).
 */
export function gridIsSingleColumn(containerPx: number | null, gapPx: number): boolean {
  return !!containerPx && containerPx < 11 * gapPx + MIN_CONTROL_WIDTH_PX
}

/** How one field sits in its section: label layout, and the width it actually takes. */
export interface FieldPlacement {
  layout: ResolvedLabelLayout
  stacked: boolean
  width: SectionWidth
}

/**
 * Resolves a field's label layout and widens the field, if need be, so its
 * control keeps its floor (MIN_CONTROL_WIDTH_PX, or minControlPx when larger).
 * Field types without a label row keep the width the author chose. Shared by
 * the renderer and the builder canvas.
 */
export function placeField(
  field: {
    type: string
    requested: SectionWidth
    labelPosition?: unknown
    labelWidth?: unknown
    /** Space beside the control that is not the control. */
    trailingPx: number
    /** The control's own floor (contentFloorPx). */
    minControlPx?: number
  },
  formDefault: FormFieldLayout | null | undefined,
  containerPx: number | null,
  gapPx: number,
): FieldPlacement {
  const singleColumn = gridIsSingleColumn(containerPx, gapPx)
  if (!supportsLabelLayout(field.type)) {
    return { layout: SYSTEM_LABEL_LAYOUT, stacked: false, width: singleColumn ? 'full' : field.requested }
  }
  const layout = resolveLabelLayout(field, formDefault)
  // A legacy checkbox draws its label inline after the box, not in a column.
  const position = field.type === 'checkbox' && layout.legacy ? 'above' : layout.position
  if (singleColumn) return { layout, stacked: position !== 'above', width: 'full' }
  const { width, stacked } = resolveFieldSpan({
    requested: field.requested,
    position,
    labelPx: LABEL_WIDTH_PX[layout.width],
    containerPx,
    gapPx,
    trailingPx: field.trailingPx,
    minControlPx: field.minControlPx,
  })
  return { layout, stacked, width }
}
